import * as is from 'Misc/is';
import * as VPAIDEvents from '../Enum/VPAIDEvents';
import * as Events from 'Services/State/Events';
import { emitMethods } from 'Emitter';
import { getMethods } from 'Misc/methods';
import { keyToArray } from 'Misc/collections';
import { nextEventLoop } from 'Misc/timing';
import { exposedSurveyPending } from 'Misc/survey';

// Implements methods and properties according to IAB VPAID spec. - http://www.iab.net/media/file/VPAID_2.0_Final_04-10-2012.pdf
export default class VPAIDInterface {
  constructor() {
    this.subscriptions = Object.values(VPAIDEvents).reduce(keyToArray, {});
    emitMethods(this, Events.HOST.MESSAGE_RECEIVED, this.receivers);
    emitMethods(this, Events.HOST.MESSAGE_SENT, this.senders);
  }

  initAd(width, height, viewMode, desiredBitrate, creativeData = {}, environmentVars = {}) {
    return this;
  }

  startAd()    { return this; }
  skipAd()     { return this; }
  stopAd()     { return this; }
  pauseAd()    { return this; }
  resumeAd()   { return this; }

  collapseAd() {
    this.expanded = false;
    this.publish(VPAIDEvents.AD_EXPANDED_CHANGE);
    return this;
  }

  expandAd() {
    this.expanded = true;
    this.publish(VPAIDEvents.AD_EXPANDED_CHANGE);
    return this;
  }

  resizeAd(width, height, viewMode) {
    this.publish(VPAIDEvents.AD_SIZE_CHANGE);
    return this;
  }

  setSize(width, height) {
    return this;
  }

  getAdExpanded() {
    return this.expanded; 
  }

  getAdLinear() {
    return true; 
  }

  getAdDuration() {
    return this.state ? this.state.media.duration : -2;
  }

  getAdRemainingTime() { 
    return this.state ?
      (this.state.media.duration > -2 ? this.state.media.duration - this.state.media.current_time : this.state.media.duration) :
      -2;
  }

  getAdSkippableState() {
    return this.state ? this.state.media.canSkip : false;
  }

  getAdVolume() {
   return this.state ? (this.state.media.volume > -2 ? this.state.media.volume : 0) : 0;
  }

  getAdCompanions() { 
    return '';
  }

  getAdIcons() {
   return false;
  }

  getAdHeight() { 
    return this.state && this.state.display ? this.state.display.height : 0;
  }

  getAdWidth() { 
    return this.state && this.state.display ? this.state.display.width : 0;
  }

  handshakeVersion() {
    return '2.0';
  }

  setAdVolume(vol) {
    return this;
  }

  subscribe(fn, event, listenerScope) {
    if(this.subscriptions[event]) {
      this.subscriptions[event].push({ fn, listenerScope });
    } else {
      console.warn('attempted to subscribe to unsupported event', event);
    }
    
    return this;
  }

  unsubscribe(fn, event) {
    // swap args if passed in wrong order
    if(is.str(fn)) {
      [fn, event] = [event, fn];
    }

    if(this.subscriptions[event]) {
      this.subscriptions[event] = this.subscriptions[event].filter( s => s.fn !== fn );
    } else {
      console.warn('attempted to unsubscribe unsupported event', fn, event);
    }
    
    return this;
  }

  publish(event, ...args) {
    this.subscriptions[event].forEach( s => s.fn.call(s.listenerScope, ...args) );
    return this;
  }

  onUpdate(nextState, prevState, changes) {
    this.state = nextState;

    if(changes.configuration.Media) {
      this.publish(VPAIDEvents.AD_LOADED);
    }

    if(changes.media.started) {
      this.publish(VPAIDEvents.AD_STARTED);
    }

    if(changes.measurement.impression) {
      this.publish(VPAIDEvents.AD_IMPRESSION);
      this.publish(VPAIDEvents.AD_VIDEO_START);
    }

    if(changes.measurement.p_25) {
      this.publish(VPAIDEvents.AD_VIDEO_FIRST_QUARTILE);
    }

    if(changes.measurement.p_50) {
      this.publish(VPAIDEvents.AD_VIDEO_MIDPOINT);
    }

    if(changes.measurement.p_75) {
      this.publish(VPAIDEvents.AD_VIDEO_THIRD_QUARTILE);
    }

    if(changes.measurement.p_100) {
      this.publish(VPAIDEvents.AD_VIDEO_COMPLETE);
    }

    if(changes.media.canSkip) {
      this.publish(VPAIDEvents.AD_SKIPPABLE_STATE_CHANGE);
    }

    if(nextState.display.skips > 0 && nextState.measurement.skipped && nextState.host.sent.indexOf(VPAIDEvents.AD_SKIPPED) === -1) {
      this.publish(VPAIDEvents.AD_SKIPPED);
    }

    if(changes.display.clicks) {
      this.publish(VPAIDEvents.AD_CLICK_THRU);
    }

    if(changes.media.paused && nextState.media.paused) {
      this.publish(VPAIDEvents.AD_PAUSED);
    }

    if(changes.media.paused && !nextState.media.paused) {
      this.publish(VPAIDEvents.AD_PLAYING);
    }

    if(changes.media.duration && nextState.media.duration > 0) {
      this.publish(VPAIDEvents.AD_DURATION_CHANGE);
    }

    if(changes.media.volume) {
      this.publish(VPAIDEvents.AD_VOLUME_CHANGE);
    }

    if(changes.errors && nextState.errors.length && nextState.measurement.error) {
      this.publish(VPAIDEvents.AD_ERROR);
    }

    if(this.canShutdown(nextState)) {
      nextEventLoop(() => {
        this.publish(VPAIDEvents.AD_STOPPED);
      });
    }
  }

  canShutdown({ media, measurement, host, configuration, errors }) {
    return (
      ( 
        this.mediaCompleted(media, measurement, configuration) || this.mediaStopped(media) || this.mediaSkipped(media, measurement)
      ) &&
      measurement.request_queue.length === 0 &&
      this.wasntSent(VPAIDEvents.AD_STOPPED, host)
    );
  }

  wasSent(message, host = this.state.host) {
    return host.sent.indexOf(message) > -1;
  }

  wasntSent(message, host) {
    return !this.wasSent(message, host);
  }

  mediaCompleted(media, measurement, configuration) {
    return media.complete && measurement.p_100 && !exposedSurveyPending(configuration, measurement);
  }

  mediaSkipped(media, measurement) {
    return media.skipped && measurement.skipped;
  }

  mediaStopped(media = {}) {
    return media.stopped || (media.received && media.received.length && media.received.indexOf(VPAIDEvents.AD_STOPPED) > -1);
  }

  get receivers() {
    const toExclude = ['publish', 'onUpdate', 'receivers', 'senders', 'canShutdown'];
    return getMethods(this).filter( m => toExclude.indexOf(m) === -1 );
  }

  get senders() {
    return ['publish'];
  }

  destroy() {
    
  }
};


// WEBPACK FOOTER //
// ./src/HostInterface/VPAIDInterface.js